/**
 * Audio Engine Component
 *
 * Bridges the player store state to actual HTML5 audio playback.
 * Handles stream URL fetching, playback controls, progress updates.
 */

import { useEffect, useRef, useState } from 'react';
import { usePlayerStore } from '@/stores/player';

export function AudioEngine() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastSongIdRef = useRef<string | null>(null);

  const {
    currentSong,
    isPlaying,
    volume,
    muted,
    repeat,
    pause,
    skipNext,
    setProgress,
    setDuration,
  } = usePlayerStore();

  // Initialize audio element once
  useEffect(() => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.preload = 'auto';
      audioRef.current = audio;

      // Event handlers
      audio.addEventListener('timeupdate', () => {
        setProgress(audio.currentTime);
      });

      audio.addEventListener('durationchange', () => {
        if (audio.duration && !isNaN(audio.duration)) {
          setDuration(audio.duration);
        }
      });

      audio.addEventListener('ended', () => {
        const state = usePlayerStore.getState();
        if (state.repeat === 'one') {
          audio.currentTime = 0;
          audio.play().catch(console.error);
        } else {
          skipNext();
        }
      });

      audio.addEventListener('error', (e) => {
        console.error('[AudioEngine] Audio error:', e);
      });

      audio.addEventListener('canplay', () => {
        console.log('[AudioEngine] Can play, isPlaying:', usePlayerStore.getState().isPlaying);
        // Auto-play when ready if isPlaying is true
        if (usePlayerStore.getState().isPlaying) {
          audio.play().catch(console.error);
        }
      });

      console.log('[AudioEngine] Audio element initialized');
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, [setProgress, setDuration, skipNext]);

  // Load stream URL when song changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!currentSong) {
      audio.pause();
      audio.src = '';
      lastSongIdRef.current = null;
      return;
    }

    // Skip if already loaded
    if (lastSongIdRef.current === currentSong.id) {
      return;
    }

    lastSongIdRef.current = currentSong.id;
    setIsLoading(true);

    console.log('[AudioEngine] Loading song:', currentSong.id, currentSong.title);

    // In Electron, use custom protocol; in web, use HTTP proxy
    const isElectron = window.location.protocol === 'file:';
    let streamUrl: string;

    if (isElectron) {
      // Use custom protocol registered in Electron main process
      // Format: zemer-stream://stream/VIDEO_ID (path preserves case, hostname doesn't)
      streamUrl = `zemer-stream://stream/${currentSong.id}`;
    } else {
      // Use HTTP stream proxy endpoint
      const API_BASE = import.meta.env.VITE_API_URL || '/api';
      streamUrl = `${API_BASE}/stream/${currentSong.id}`;
    }

    console.log('[AudioEngine] Using stream URL:', streamUrl);
    audio.src = streamUrl;
    audio.load();
    setIsLoading(false);
  }, [currentSong?.id, skipNext]);

  // Handle play/pause state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentSong || isLoading) return;

    if (isPlaying) {
      if (audio.src && audio.readyState >= 2) {
        console.log('[AudioEngine] Playing');
        audio.play().catch((e) => {
          console.warn('[AudioEngine] Play failed:', e);
        });
      } else {
        console.log('[AudioEngine] Waiting for audio to be ready, readyState:', audio.readyState);
      }
    } else {
      console.log('[AudioEngine] Pausing');
      audio.pause();
    }
  }, [isPlaying, currentSong, isLoading]);

  // Handle volume/mute
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = muted ? 0 : volume;
  }, [volume, muted]);

  // Handle seek
  useEffect(() => {
    const unsubscribe = usePlayerStore.subscribe((state, prevState) => {
      if (
        audioRef.current &&
        state.progress !== prevState.progress &&
        Math.abs(state.progress - audioRef.current.currentTime) > 1
      ) {
        audioRef.current.currentTime = state.progress;
      }
    });

    return unsubscribe;
  }, []);

  // Setup Media Session handlers
  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', () => usePlayerStore.getState().resume());
      navigator.mediaSession.setActionHandler('pause', () => pause());
      navigator.mediaSession.setActionHandler('previoustrack', () => usePlayerStore.getState().skipPrevious());
      navigator.mediaSession.setActionHandler('nexttrack', () => skipNext());
      navigator.mediaSession.setActionHandler('seekto', (details) => {
        if (audioRef.current && details.seekTime !== undefined) {
          audioRef.current.currentTime = details.seekTime;
          setProgress(details.seekTime);
        }
      });
    }
  }, [pause, skipNext, setProgress]);

  return null;
}
